import { DocumentNode } from 'graphql';
import { Route, Switch, useHistory } from 'react-router-dom';
import { Tag, Trash, Users } from 'react-feather';
import { useTranslation } from 'react-i18next';

import {
  GetInvitationQuery as GetInvitation,
  GetInvitationsQuery as GetInvitations,
  PurchaseStatus,
} from '__generated__/graphql';

import { Option } from '../../../../common/Common/OptionsDropdown';
import { encodeUrl } from '../../../../common/helpers';
import { useInvitationStatus } from './InvitationStatusIndicator';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import ActivateInvitationMutation from './ActivateInvitationMutation';
import CardPreview from '../CardPreview';
import DeactivateInvitationMutation from './DeactivateInvitationMutation';
import DeleteInvitationMutation from './DeleteInvitationMutation';
import EditInvitationForm from './EditInvitationForm';
import GetInvitationsQuery from './GetInvitationsQuery';
import ShareInfo from './ShareInfo';
import UI from '../../../../common/UI';
import useConfirmModal from '../../../../common/useConfirmModal';
import useLocale from '../../../../common/useLocale';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../../common/useProject';

  type Event = GetInvitations['project']['event'];
  type Invitation = Event['invitations'][0];

interface InvitationListProps {
  event: Event;
  onResetFilters: () => void;
  activeInvitationId: string;
  invitationData?: GetInvitation;
  refetchQueries?: DocumentNode[];
}

const InvitationList = ({
  event, onResetFilters, activeInvitationId, invitationData, refetchQueries = [GetInvitationsQuery],
}: InvitationListProps) => {
  const project = useProject();
  const { t } = useTranslation();

  const hasFilteredInvitations = event.invitations.length > 0;

  return (
    <UI.Div>
      {hasFilteredInvitations ? (
        <UI.GridContainer sc={{ gutter: 1 / 3 }}>
          {event.invitations.map((invitation, index) => (
            <UI.CollapsibleNavCard
              id={`InvitationCard_${invitation.id}`}
              key={index}
              to={`/${project.id}/events/${event.id}/invitation-codes/${activeInvitationId !== invitation.id ? invitation.id : ''}`}
              open={invitation.id === activeInvitationId}
              muted={!!activeInvitationId && invitation.id !== activeInvitationId}
              preview={(
                <InvitationPreview
                  invitation={invitation}
                  event={event}
                  refetchQueries={refetchQueries}
                />
              )}
              body={invitationData
                ? <InvitationBody invitationData={invitationData} />
                : <UI.PageLoader />}
            />
          ))}
        </UI.GridContainer>
      ) : (
        <UI.Card
          sc={{ padding: 8, textAlign: 'center' }}
        >
          <UI.Div sc={{ color: 'secondary', mb: 2 }}>
            <Tag strokeWidth={0.5} size={60} />
          </UI.Div>
          <UI.Span>{t('no_invitations_match_filters')}</UI.Span>
          {' '}
          <UI.A onClick={onResetFilters}>{t('reset_filters')}</UI.A>
        </UI.Card>
      )}
    </UI.Div>
  );
};

interface InvitationPreviewProps {
  invitation: Invitation;
  event: Event;
  refetchQueries?: DocumentNode[];
}

const InvitationPreview = ({ invitation, event, refetchQueries }: InvitationPreviewProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const notifier = useNotifier();
  const history = useHistory();
  const project = useProject();

  const [activateInvitation] = useMutation(ActivateInvitationMutation, {
    variables: {
      id: invitation.id,
    },
    optimisticResponse: {
      activateInvitation: {
        id: invitation.id,
        active: true,
        __typename: 'Invitation',
      },
    },
    onCompleted: () => notifier.success(t('notifications.invitation_activated')),
  });

  const [deactivateInvitation] = useMutation(DeactivateInvitationMutation, {
    variables: {
      id: invitation.id,
    },
    optimisticResponse: {
      deactivateInvitation: {
        id: invitation.id,
        active: false,
        __typename: 'Invitation',
      },
    },
    onCompleted: () => notifier.success(t('notifications.invitation_deactivated')),
  });

  const toggleStatus = () => {
    if (invitation.active) {
      deactivateInvitation();
    } else {
      activateInvitation();
    }
  };

  const [deleteInvitation, { loading: deleting }] = useMutation(
    DeleteInvitationMutation, {
      variables: {
        id: invitation.id,
      },
      onCompleted: () => {
        history.push(`/${project.id}/events/${event.id}/invitation-codes`);
        notifier.success(t('notifications.invitation_deleted'));
      },
      onError: () => notifier.error(t('something_went_wrong')),
      refetchQueries,
      awaitRefetchQueries: refetchQueries.length > 0,
    },
  );

  const [confirmDelete] = useConfirmModal({
    title: t('delete_name', { name: invitation.prefix }),
    onConfirm: () => deleteInvitation(),
  }, [invitation]);

  const title = `${invitation.prefix}${invitation.has_unique_codes ? '········' : ''}`;
  const { message } = useInvitationStatus(invitation);

  return (
    <CardPreview
      title={title}
      secondColumn={(
        <UI.TooltipContainer sc={{ inlineBlock: true, padding: 1, margin: -1 }}>
          <UI.Link
            to={`/${project.id}/participants?${encodeUrl({
              event: event.id,
              invitations: [invitation.id],
              statuses: [PurchaseStatus.complete, PurchaseStatus.incomplete],
            })}`}
            sc={{ secondary: true }}
          >
            <UI.Icon>
              <Users />
            </UI.Icon>
            {' '}
            {` ${formatInteger(invitation.uses)} / ${invitation.max_uses !== null ? formatInteger(invitation.max_uses) : '∞'}`}
          </UI.Link>
          <UI.Tooltip sc={{ alignH: 'center' }}>
            {t('times_used')}
          </UI.Tooltip>
        </UI.TooltipContainer>
      )}
      status={message}
      active={invitation.active}
      onToggle={toggleStatus}
      options={() => (
        <Option
          icon={<Trash />}
          label={t('delete')}
          onClick={() => confirmDelete()}
          disabled={!invitation.deletable || deleting}
        />
      )}
    />
  );
};

interface InvitationBodyProps {
  invitationData: GetInvitation;
}

const InvitationBody = ({ invitationData: { invitation, event } }: InvitationBodyProps) => {
  const { t } = useTranslation();
  const project = useProject();

  return (
    <UI.FormGrid sc={{ columns: ['1fr', '1fr 3fr'], gutter: 1.5 }}>
      <UI.Nav>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/invitation-codes/${invitation.id}`}
          exact
        >
          {t('settings')}
        </UI.NavLink>
        <UI.NavLink
          to={`/${project.id}/events/${event.id}/invitation-codes/${invitation.id}/share`}
        >
          {t('share')}
        </UI.NavLink>
      </UI.Nav>

      <UI.Div>
        <Switch>
          <Route path="/:projectId/events/:eventId/invitation-codes/:invitationId/share">
            <ShareInfo invitation={invitation} />
          </Route>
          <Route path="/:projectId/events/:eventId/invitation-codes/:invitationId">
            <EditInvitationForm invitation={invitation} event={event} />
          </Route>
        </Switch>
      </UI.Div>
    </UI.FormGrid>
  );
};

export default InvitationList;
